import type { ConversationOption, SummaryCategory } from "@samklang/schemas";

interface QuestionDetails {
  readonly section?: string;
  readonly theme?: string;
  readonly supportText?: string;
  readonly professionalPrompt?: string;
  readonly suggestedFollowUps?: readonly string[];
  readonly alternativeWording?: readonly string[];
  readonly wordingByAudience?: Readonly<Partial<Record<"younger" | "older" | "teen" | "adult" | "enkel" | "standard", string>>>;
  readonly wordingByPerspective?: Readonly<Partial<Record<"elev" | "mentor" | "vardnadshavare" | "annan", string>>>;
  readonly summaryCategory?: SummaryCategory;
  readonly showWhen?: { readonly questionId: string; readonly includesOptionId?: string; readonly answerKind?: "answered" | "skipped" };
  readonly safetyRuleId?: string;
  readonly priority?: number;
  readonly otherPrompt?: string;
  readonly metric?: { readonly key: string; readonly label: string; readonly direction?: "higher_is_better" | "higher_is_more" };
}

const base = (id: string, title: string, details: QuestionDetails) => ({
  id,
  title,
  theme: details.theme ?? details.section ?? "Övrigt",
  section: details.section ?? "Övrigt",
  supportText: details.supportText,
  professionalPrompt: details.professionalPrompt,
  suggestedFollowUps: details.suggestedFollowUps ?? [],
  alternativeWording: details.alternativeWording ?? [],
  wordingByAudience: details.wordingByAudience,
  wordingByPerspective: details.wordingByPerspective,
  summaryMapping: { category: details.summaryCategory ?? "other" },
  showWhen: details.showWhen,
  safetyRuleId: details.safetyRuleId,
  priority: details.priority ?? 50,
  otherPrompt: details.otherPrompt,
  metric: details.metric,
});

const otherOption: ConversationOption = { id: "other", label: "Annat", symbol: "…", isOther: true };

function withOther(options: readonly ConversationOption[]): readonly ConversationOption[] {
  if (options.some((option) => option.isOther)) return options;
  if (options.some((option) => option.id === "other")) {
    return options.map((option) => option.id === "other" ? { ...option, isOther: true } : option);
  }
  return [...options, otherOption];
}

export const feelingOptions: readonly ConversationOption[] = [
  { id: "very-good", label: "Väldigt bra", symbol: "😄", tone: "positive" },
  { id: "good", label: "Bra", symbol: "🙂", tone: "positive" },
  { id: "okay", label: "Sådär", symbol: "😐", tone: "neutral" },
  { id: "hard", label: "Jobbigt", symbol: "🙁", tone: "warning" },
  { id: "very-hard", label: "Väldigt jobbigt", symbol: "😣", tone: "negative" },
];

export const agreementOptions: readonly ConversationOption[] = [
  { id: "not-at-all", label: "Stämmer inte", symbol: "○" },
  { id: "a-little", label: "Stämmer lite", symbol: "◔" },
  { id: "mostly", label: "Stämmer ganska bra", symbol: "◕" },
  { id: "fully", label: "Stämmer helt", symbol: "●" },
];

export const yesNoOptions: readonly ConversationOption[] = [
  { id: "yes", label: "Ja", symbol: "✓", tone: "positive" },
  { id: "sometimes", label: "Ibland", symbol: "◐", tone: "neutral" },
  { id: "no", label: "Nej", symbol: "○", tone: "negative" },
];

export const energyOptions: readonly ConversationOption[] = [
  { id: "gives", label: "Ger energi", symbol: "＋", tone: "positive" },
  { id: "neutral", label: "Varken eller", symbol: "＝", tone: "neutral" },
  { id: "takes", label: "Tar energi", symbol: "−", tone: "warning" },
];

export const feeling = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "feeling", ...base(id, title, details), options: feelingOptions });
export const agreement = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "agreement", ...base(id, title, details), options: agreementOptions });
export const choice = (id: string, title: string, options: readonly ConversationOption[], details: QuestionDetails = {}) => ({ kind: "choice", ...base(id, title, details), options: withOther(options) });
export const yesNo = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "yes_no", ...base(id, title, details), options: yesNoOptions });
export const multi = (id: string, title: string, options: readonly ConversationOption[], details: QuestionDetails & { readonly maxSelections?: number } = {}) => ({ kind: "multi_choice", ...base(id, title, details), options: withOther(options), maxSelections: details.maxSelections });
export const scale = (id: string, title: string, lowLabel: string, highLabel: string, details: QuestionDetails = {}) => ({ kind: "scale", ...base(id, title, details), min: 0, max: 10, lowLabel, highLabel });
export const scaleFive = (id: string, title: string, lowLabel: string, highLabel: string, details: QuestionDetails = {}) => ({ kind: "scale", ...base(id, title, details), min: 1, max: 5, lowLabel, highLabel });
export const energy = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "energy", ...base(id, title, details), options: energyOptions });
export const text = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "text", ...base(id, title, details), placeholder: "Skriv om du vill", maxLength: 1000 });
export const tell = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "tell", ...base(id, title, details), prompt: "Berätta, peka eller visa på ditt sätt." });
export const ranking = (id: string, title: string, options: readonly ConversationOption[], details: QuestionDetails = {}) => ({ kind: "ranking", ...base(id, title, details), options });
export const imageChoice = (id: string, title: string, options: readonly (ConversationOption & { readonly altText: string })[], details: QuestionDetails = {}) => ({ kind: "image_choice", ...base(id, title, details), options });
export const personMap = (id: string, title: string, people: readonly ConversationOption[], details: QuestionDetails = {}) => ({ kind: "person_map", ...base(id, title, details), people });
export const drawing = (id: string, title: string, details: QuestionDetails = {}) => ({ kind: "drawing", ...base(id, title, details), prompt: "Rita om du vill. Ritningen stannar i det tillfälliga samtalet." });
export const previousAnswer = (id: string, title: string, sourceQuestionIds: readonly string[], details: QuestionDetails = {}) => ({ kind: "previous_answer", ...base(id, title, details), sourceQuestionIds, placeholder: "Skriv om du vill lägga till något" });
